// Generic in-app confirmation — replaces the native window.confirm
// "<host> says" prompt for destructive actions (remove member, leave
// workspace, revoke invitation, etc.). Reuse this for any "are you
// sure?" yes/no flow that doesn't need type-to-confirm friction
// (use ArchiveWorkspaceDialog for that).

import {
  Button,
  Dialog,
  HStack,
  Portal,
  Text,
  type ButtonProps,
} from "@chakra-ui/react";
import type { ReactNode } from "react";

interface Props {
  open: boolean;
  title: string;
  /** Body — text or any React node (lets the caller embed bold names etc.). */
  description: ReactNode;
  /** Default "Confirm". */
  confirmLabel?: string;
  /** Default "Cancel". */
  cancelLabel?: string;
  /** Default "red". Use "blue" / "gray" for non-destructive prompts. */
  confirmPalette?: ButtonProps["colorPalette"];
  submitting?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

export default function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmPalette = "red",
  submitting = false,
  onOpenChange,
  onConfirm,
}: Props) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(d) => onOpenChange(d.open)}
      closeOnInteractOutside={false}
      size="sm"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>{title}</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              {typeof description === "string" ? (
                <Text fontSize="sm">{description}</Text>
              ) : (
                description
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={2}>
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={submitting}
                >
                  {cancelLabel}
                </Button>
                <Button
                  colorPalette={confirmPalette}
                  loading={submitting}
                  onClick={onConfirm}
                >
                  {confirmLabel}
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
